import * as captcha from '../../Util/captcha.js';
import { handleErrors, simplePostRequestWithBody } from "../../API/baseAPI.js";
import { showAlert } from "../../Util/alerts.js";
import { hideModal, showModal } from "../../Util/helper.js";
import { isUserLoggedIn } from '../../UIController/user.js';

export class ReportManager {
    constructor() {
        this.currentStep = 1;
        this.totalSteps = 4;
        this.stepsCompleted = [false, false, false, false];
        this.report = {
            type: null,
            target: {
                username: null,
                contentId: null
            },
            category: null,
            reason: null,
            description: null,
            reported: {
                user: null,
                content: null,
            },
            reporterEmail: null
        };
    }

    static getInstance() {
        if (!ReportManager.instance) {
            ReportManager.instance = new ReportManager();
        }
        return ReportManager.instance;
    }

    initReportSystem() {
        if (isUserLoggedIn()) {
            this.report.reporterEmail = alertUser.email;
            document.getElementById('reporterEmail').value = alertUser.email;
            document.getElementById('reporterEmail').disabled = true;
        }
    }

    showReportModal(config = {}) {
        this.resetReportForm();
        if (config.type) {
            this.report.type = config.type;
            this.highlightSelectedButton(`${config.type}TypeBtn`, 'reportTypeStep');
            
            if (config.type === 'user' && config.userId) {
                this.report.reported.user = config.userId;
                this.report.target.username = config.userName || config.userId;
                document.getElementById('targetUsername').value = this.report.target.username;
                document.getElementById('targetUsername').disabled = true;
                this.showTargetInput('user');
                this.stepsCompleted[0] = true;
            } else if (config.type === 'content' && config.contentId) {
                this.report.reported.content = config.contentId;
                this.report.target.contentId = config.contentId;
                document.getElementById('targetContentId').value = this.report.target.contentId;
                document.getElementById('targetContentId').disabled = true;

                if (config.userId || config.userName) {
                    this.report.reported.user = config.userId || config.userName;
                    document.getElementById('targetUsername').value = config.userName || config.userId;
                    document.getElementById('targetUsername').disabled = true;
                }

                this.showTargetInput('content');
                this.stepsCompleted[0] = true;
            } else {
                this.showTargetInput(config.type);
            }
        }
        
        this.updateStepIndicators();

        if (this.currentStep === 4) {
            this.initializeReportCaptcha();
        }
        
        showModal('reportModal');
    }

    async initializeReportCaptcha() {
        await captcha.initializeCaptcha({
            containerId: 'reportCaptcha',
            submitButtonId: 'reportSubmitButton',
        });
    }

    resetReportForm() {
        this.report = {
            type: null,
            target: {
                username: null,
                contentId: null
            },
            category: null,
            reason: null,
            description: null,
            reported: {
                user: null,
                content: null,
                rating: null,
                place: null
            },
            reporterEmail: null,
        };
        
        this.currentReportStep = 1;
        this.stepsCompleted = [false, false, false, false];
        document.getElementById('reportDescription').value = '';
        document.getElementById('targetUsername').value = '';
        document.getElementById('targetContentId').value = '';
        
        document.getElementById('reportModal').querySelectorAll('.btn-primary').forEach(button => {
            if (!button.id.includes('reportSubmitButton')) {
                button.classList.add('btn-outline');
            }
        });
        
        document.querySelectorAll('.step-content').forEach(el => {
            el.classList.add('hidden');
        });
        document.getElementById('reportTypeStep').classList.remove('hidden');
        
        document.getElementById('reportTargetInput').classList.add('hidden');
        document.getElementById('userTargetInput').classList.add('hidden');
        document.getElementById('contentTargetInput').classList.add('hidden');
        
        document.getElementById('reportSubmitButton').classList.add('hidden');
        document.getElementById('reportBack').classList.add('hidden');
        
        if (isUserLoggedIn()) {
            this.report.reporterEmail = alertUser.email;
            document.getElementById('reporterEmail').value = alertUser.email;
        } else {
            document.getElementById('reporterEmail').disabled = false;
        }
        
        this.updateStepIndicators();
    }

    selectReportType(type) {
        this.report.type = type;
        
        document.getElementById('selectedTypeDisplay').textContent = 
            type === 'user' ? 'User' : 'Content';
        this.highlightSelectedButton(`${type}TypeBtn`, 'reportTypeStep');
        
        this.showTargetInput(type);
    }

    highlightSelectedButton(selectedButtonId, parentContainerId) {
        const container = document.getElementById(parentContainerId);
        const buttons = container.querySelectorAll('.btn');
        
        buttons.forEach(button => {
            button.classList.add('btn-outline');
        });
        
        const selectedButton = document.getElementById(selectedButtonId);
        if (selectedButton) {
            selectedButton.classList.remove('btn-outline');
        }
    }

    showTargetInput(type) {
        document.getElementById('reportTargetInput').classList.remove('hidden');
        document.getElementById('userTypeBtn').classList.add('btn-outline');
        document.getElementById('contentTypeBtn').classList.add('btn-outline');

        if (type === 'user') {
            document.getElementById('userTargetInput').classList.remove('hidden');
            document.getElementById('contentTargetInput').classList.add('hidden');
            document.getElementById('userTypeBtn').classList.remove('btn-outline');
        } else if (type === 'content') {
            document.getElementById('userTargetInput').classList.add('hidden');
            document.getElementById('contentTargetInput').classList.remove('hidden');
            document.getElementById('contentTypeBtn').classList.remove('btn-outline');
        }
    }

    confirmTargetSelection() {
        if (this.report.type === 'user') {
            const username = document.getElementById('targetUsername').value.trim();
            if (!username) {
                showAlert('Please enter a username.', 'warning');
                return;
            }
            this.report.target.username = username;
            this.report.reported.user = username;
        } else if (this.report.type === 'content') {
            const contentId = document.getElementById('targetContentId').value.trim();
            if (!contentId) {
                showAlert('Please enter a content-id.', 'warning');
                return;
            }
            this.report.target.contentId = contentId;
            this.report.reported.content = contentId;
        }
        
        this.stepsCompleted[0] = true;
        this.goToNextReportStep();
    }

    selectReasonCategory(category) {
        this.report.category = category;
        
        document.getElementById('selectedCategoryDisplay').textContent = getReasonCategoryName(category);
        document.getElementById('selectedTypeDisplay').textContent = this.report.type === 'user' ? 'User' : 'Content';
        document.getElementById('summaryCategory').textContent = getReasonCategoryName(category);
        
        this.highlightSelectedButton(`${category}Category`, 'reportReasonCategoryStep');
        this.showSpecificReasons(category);
        
        hideAllStepContents();
        document.getElementById('reportSpecificReasonStep').classList.remove('hidden');
    }

    showSpecificReasons(category) {
        document.querySelectorAll('[id$="Reasons"]').forEach(el => {
            el.classList.add('hidden');
        });
        
        document.getElementById(`${category}Reasons`).classList.remove('hidden');
    }

    selectSpecificReason(reason) {
        this.report.reason = reason;
        document.getElementById('selectedReasonDisplay').textContent = getReasonDisplayName(reason);
        this.stepsCompleted[1] = true;
        this.goToNextReportStep();
    }

    async goToNextReportStep() {
        if (!this.validatecurrentReportStep()) {
            return;
        }
        
        hideAllStepContents();
        this.currentReportStep++;
    
        if (this.currentReportStep === 4) {
            this.initializeReportCaptcha();
        }
        
        switch(this.currentReportStep) {
            case 2:
                document.getElementById('reportReasonCategoryStep').classList.remove('hidden');
                if (this.report.type === 'user') {
                    document.getElementById('userCategories').classList.remove('hidden');
                    document.getElementById('contentCategories').classList.add('hidden');
                } else {
                    document.getElementById('userCategories').classList.add('hidden');
                    document.getElementById('contentCategories').classList.remove('hidden');
                }
                break;
            case 3:
                document.getElementById('reportDetailStep').classList.remove('hidden');
                break;
            case 4:
                document.getElementById('reportConfirmStep').classList.remove('hidden');
                this.prepareSummary();
                break;
        }
        
        this.updateReportNavigationButtons();
        this.updateStepIndicators();
    }

    gotToPreviousReportStep() {
        hideAllStepContents();
        if (this.currentReportStep === 2) {
            if (this.report.category) {
                this.report.category = null;
                this.report.reason = null;
                document.getElementById('reportReasonCategoryStep').classList.remove('hidden');
                if (this.report.type === 'user') {
                    document.getElementById('userCategories').classList.remove('hidden');
                    document.getElementById('contentCategories').classList.add('hidden');
                } else {
                    document.getElementById('userCategories').classList.add('hidden');
                    document.getElementById('contentCategories').classList.remove('hidden');
                }
            } else {
                this.currentReportStep--;
                document.getElementById('reportTypeStep').classList.remove('hidden');
            }
        } else {
            this.currentReportStep--;
            switch(this.currentReportStep) {
                case 1:
                    document.getElementById('reportTypeStep').classList.remove('hidden');
                    break;
                case 2:
                    if (!this.report.category) {
                        document.getElementById('reportReasonCategoryStep').classList.remove('hidden');
                    } else {
                        document.getElementById('reportSpecificReasonStep').classList.remove('hidden');
                    }
                    break;
                case 3:
                    document.getElementById('reportDetailStep').classList.remove('hidden');
                    break;
            }
        }
        
        this.updateReportNavigationButtons();
        this.updateStepIndicators();
    }

    updateReportNavigationButtons() {
        const backButton = document.getElementById('reportBack');
        const submitButton = document.getElementById('reportSubmitButton');
        
        if (this.currentReportStep > 1) {
            backButton.classList.remove('hidden');
        } else {
            backButton.classList.add('hidden');
        }
        
        if (this.currentReportStep < this.totalSteps) {
            submitButton.classList.add('hidden');
        } else {
            submitButton.classList.remove('hidden');
        }
    }

    navigateToStep(step) {
        if (step <= this.currentReportStep || this.stepsCompleted[step-1]) {
            hideAllStepContents();
            
            if (step == 2 && this.currentReportStep == 2) {
                this.report.category = null;
                this.report.reason = null;
            }
    
            this.currentReportStep = step;
            
            switch(step) {
                case 1:
                    document.getElementById('reportTypeStep').classList.remove('hidden');
                    break;
                case 2:
                    if (this.report.reason) {
                        document.getElementById('reportSpecificReasonStep').classList.remove('hidden');
                    } 
                    else if (this.report.category) {
                        document.getElementById('reportSpecificReasonStep').classList.remove('hidden');
                    }
                    else {
                        document.getElementById('reportReasonCategoryStep').classList.remove('hidden');
                        
                        if (this.report.type === 'user') {
                            document.getElementById('userCategories').classList.remove('hidden');
                            document.getElementById('contentCategories').classList.add('hidden');
                        } else {
                            document.getElementById('userCategories').classList.add('hidden');
                            document.getElementById('contentCategories').classList.remove('hidden');
                        }
                    }
                    break;
                case 3:
                    document.getElementById('reportDetailStep').classList.remove('hidden');
                    break;
                case 4:
                    document.getElementById('reportConfirmStep').classList.remove('hidden');
                    this.prepareSummary();
                    break;
            }
            
            this.updateReportNavigationButtons();
            this.updateStepIndicators();
        } else if ((this.currentReportStep + 1) == step) {
            this.goToNextReportStep();
        } else {
            showAlert('Please complete the previous steps first.', 'warning');
        }
    }

    updateStepIndicators() {
        for (let i = 1; i <= this.totalSteps; i++) {
            const indicator = document.getElementById(`step${i}Indicator`);
            
            if (!indicator) continue;
    
            indicator.classList.remove('step-primary');
            
            if (i < this.currentReportStep || this.stepsCompleted[i-1]) {
                indicator.classList.add('step-primary');
            } else if (i === this.currentReportStep) {
                indicator.classList.add('step-primary');
            }
        }
    }

    validatecurrentReportStep() {
        switch (this.currentReportStep) {
            case 1:
                if (!this.report.type) {
                    showAlert('Please select a report type.', 'warning');
                    return false;
                }
                
                if (this.report.type === 'user' && !this.report.target.username) {
                    showAlert('Please enter a username.', 'warning');
                    return false;
                }
                
                if (this.report.type === 'content' && !this.report.target.contentId) {
                    showAlert('Please enter a content-id.', 'warning');
                    return false;
                }
                
                this.stepsCompleted[0] = true;
                return true;
                
            case 2:
                if (!document.getElementById('reportSpecificReasonStep').classList.contains('hidden')) {
                    if (!this.report.reason) {
                        showAlert('Please select a reason for your report.', 'warning');
                        return false;
                    }
                    return true;
                } else {
                    if (!this.report.category) {
                        showAlert('Please select the category of your report.', 'warning');
                        return false;
                    }
                    return true;
                }
    
            case 3:
                this.report.description = document.getElementById('reportDescription').value.trim();
                
                if (!this.report.description) {
                    showAlert('Please provide a description for your report.', 'warning');
                    return false;
                }
                
                return true;
    
            default:
                return true;
        }
    }

    prepareSummary() {
        document.getElementById('summaryType').textContent = this.report.type === 'user' ? 'User' : 'Content';
        document.getElementById('summaryTarget').textContent = this.report.type === 'user' ? this.report.target.username : this.report.target.contentId;
        document.getElementById('summaryCategoryFinal').textContent = getReasonCategoryName(this.report.category);
        document.getElementById('summaryReason').textContent = getReasonDisplayName(this.report.reason);
        document.getElementById('summaryDescription').textContent = this.report.description;
        document.getElementById('summaryEmail').textContent = this.report.reporterEmail || 'Nicht provided';
    }

    cancelReport() {
        captcha.cleanupCaptcha('reportCaptcha');
        hideModal('reportModal');
        this.resetReportForm();
    }

    submitReport() {
        const submitButton = document.getElementById('reportSubmitButton');
        
        const reportData = {
            type: this.report.type,
            reason: this.report.category,
            subReason: this.report.reason,
            description: this.report.description,
            reportedUser: this.report.reported.user,
            reportedContent: this.report.reported.content,
            reportedRating: this.report.reported.rating,
            reportedPlace: this.report.reported.place,
            reporterEmail: this.report.reporterEmail,
            captchaResponse: captcha.getCaptchaResponse('reportCaptcha'),
            sitekey: captcha.sitekey
        };
        
        simplePostRequestWithBody('/api/v1/report', reportData, 'reportSubmitButton', (response) => {
            captcha.resetCaptcha('reportCaptcha');
            submitButton.disabled = false;
            
            if (response.error) {
                handleErrors(response);
                return;
            }

            showAlert(response.message || 'Report was submitted successfully.', 'success');
            hideModal('reportModal');
            this.resetReportForm();
        });
    }
}

function getReasonCategoryName(categoryCode) {
    const categoryMap = {
        'personal': 'Personal violations',
        'content': 'Content violations',
        'technical': 'Technical problems',
        'misinformation': 'Misinformation',
        'legal': 'Legal violations',
        'policy': 'Policy violations',
        'other': 'Other'
    };
    
    return categoryMap[categoryCode] || categoryCode;
}

function getReasonDisplayName(reasonCode) {
    const reasonMap = {
        'impersonation': 'Identity theft',
        'harassment': 'Harassment',
        'threats': 'Threats',
        'privacy': 'Privacy invasion',
        'inappropriate': 'Inappropriate content',
        'spam': 'Spam',
        'offensive': 'Offensive content',
        'harmful': 'Harmful content',
        'bot': 'Bot account',
        'bug': 'Bug exploitation',
        'glitch': 'Technical errors',
        'false_facts': 'False facts',
        'misleading': 'Misleading representation',
        'outdated': 'Outdated information',
        'copyright': 'Copyright infringement',
        'defamation': 'Defamation',
        'trademark': 'Trademark infringement',
        'illegal_content': 'Illegal content',
        'tos_violation': 'Violation of terms of use',
        'community_guidelines': 'Violation of community guidelines',
        'fake_content': 'Fake content',
        'other': 'Other reason'
    };
    
    return reasonMap[reasonCode] || reasonCode;
}

function hideAllStepContents() {
    document.querySelectorAll('.step-content').forEach(el => {
        el.classList.add('hidden');
    });
}